import React, { useMemo } from 'react';
import { DataGridPremium, GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { baseGetRowClassName, baseGridSx } from '../../../../components/Table/gridStyles';
import { useFetchAlpacaRequestFeesQuery } from '../../../../redux/features/alpacaFee/alpacaFee.apiSlice';
import { AlpacaFeeRowSource, AlpacaFeeShare, AlpacaFeeType } from '../../../../types/alpacaFee';
import { formatDateTime } from '../../../../utils/date';
import { round2Format } from '../../../../utils/number';

const usd = (value: number | null | undefined) => {
  if (value === null || value === undefined) {
    return '';
  }

  return `$${round2Format(Number(value))}`;
};

/** REG is worked out on the sell value and the wire share on the order's USD; TAF and CAT on shares. */
const formatBase = (row: AlpacaFeeShare) => {
  if (row.feeType === AlpacaFeeType.TAF || row.feeType === AlpacaFeeType.CAT) {
    return `${Number(row.base)} shares`;
  }

  return usd(row.base);
};

const sumOf = (rows: AlpacaFeeShare[], field: 'feeAmount' | 'alpacaPart' | 'platformPart') =>
  rows.reduce((total, row) => total + Number(row[field]), 0);

const COLUMNS: GridColDef<AlpacaFeeShare>[] = [
  { field: 'feeType', headerName: 'Fee', width: 190, sortable: false },
  {
    field: 'source',
    headerName: 'Source',
    width: 100,
    sortable: false,
    renderCell: ({ value }: GridRenderCellParams<AlpacaFeeShare>) => (
      <Chip
        size="small"
        label={value}
        color={value === AlpacaFeeRowSource.MANUAL ? 'primary' : 'default'}
        variant="outlined"
      />
    ),
  },
  {
    field: 'base',
    headerName: 'Worked out on',
    width: 140,
    sortable: false,
    valueGetter: ({ row }) => formatBase(row),
  },
  {
    field: 'feeAmount',
    headerName: 'User paid',
    type: 'number',
    width: 100,
    sortable: false,
    renderCell: ({ value }) => usd(value),
  },
  {
    field: 'alpacaPart',
    headerName: 'Alpaca part',
    type: 'number',
    width: 100,
    sortable: false,
    renderCell: ({ value }) => usd(value),
  },
  {
    field: 'platformPart',
    headerName: 'Ours',
    type: 'number',
    width: 90,
    sortable: false,
    renderCell: ({ value }) => usd(value),
  },
  {
    field: 'appliedAt',
    headerName: 'Taken',
    width: 170,
    sortable: false,
    valueGetter: ({ value }) => (value ? formatDateTime(value) : 'Not taken'),
  },
  { field: 'tradeDate', headerName: 'Trade date (NY)', width: 130, sortable: false },
];

interface AlpacaOrderFeesProps {
  /** The request the order belongs to — fees are loaded once per request. */
  madkholOrderId: string;
  /** The `order` row to show fees for. */
  orderId: number;
}

/**
 * The Alpaca fees charged on one order: REG, TAF and CAT, and its share of the redemption wire fee.
 * `Not taken` = saved only (SHADOW) or not taken yet. Ours is negative when we covered part of Alpaca's cost.
 */
export function AlpacaOrderFees({ madkholOrderId, orderId }: AlpacaOrderFeesProps) {
  const { data, isFetching, isError } = useFetchAlpacaRequestFeesQuery(madkholOrderId);
  const rows = useMemo(() => (data || []).filter((row) => row.orderId === orderId), [data, orderId]);

  if (isError) {
    return <Alert severity="error">Could not load the fees.</Alert>;
  }

  return (
    <>
      <DataGridPremium
        autoHeight
        rows={rows}
        columns={COLUMNS}
        loading={isFetching}
        getRowClassName={baseGetRowClassName}
        sx={baseGridSx}
        localeText={{ noRowsLabel: 'No fees on this order' }}
        disableColumnFilter
        disableColumnMenu
        hideFooter
      />
      {rows.length > 0 && (
        <Stack direction="row" spacing={3} sx={{ px: 2, py: 1.5 }}>
          <Typography variant="body2">
            <b>Total</b>
          </Typography>
          <Typography variant="body2">User paid: {usd(sumOf(rows, 'feeAmount'))}</Typography>
          <Typography variant="body2">Alpaca part: {usd(sumOf(rows, 'alpacaPart'))}</Typography>
          <Typography variant="body2">Ours: {usd(sumOf(rows, 'platformPart'))}</Typography>
        </Stack>
      )}
    </>
  );
}
